'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import classNames from 'classnames';
import style from './mobile-menu.module.scss';

import { setChannel, setUser } from '../../store/slices/user/UserSlice';
import { signOutServiceV2 } from '@/src/services/auth';
import { useAppSelector } from '../../hooks/useAppSelector/UseAppSelector';
import { useCheckIsMobileView } from '../../hooks/useCheckIsMobileView/useCheckIsMobileView';
import { useDispatch } from 'react-redux';
import { useUserSelector } from '../../store/selectors';

import Avatar from '@/src/components/clean/Avatar/Avatar';
import CreateEventMobileModal from '../Header/createEvent/createEventMobileModal';
import LogoIcon from '../../assets/icons/LogoIcon';
import Link from 'next/link';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Button from '../../components/Button/Button';
import Icon from '../../components/Icon';

type Props = {
  handleMenuClose: () => void;
};

export const logout = async () => {
  const channel = new BroadcastChannel('app_channel');
  await signOutServiceV2();
  channel.postMessage('user_logged_out');
};

const MobileMenu = ({ handleMenuClose }: Props) => {
  const user = useAppSelector(useUserSelector);
  const [openMobileModal, setOpenMobileModal] = useState(false);
  const { width } = useCheckIsMobileView();
  const router = useRouter();
  const dispatch = useDispatch();
  const staticUrl = process.env.NEXT_PUBLIC_ASSET_CDN;
  const avatarUrl = `${staticUrl}${user?.avatar}`;

  const pathname = usePathname();
  const handlePopup = () => {
    if (width < 1280) {
      setOpenMobileModal(true);
      return;
    }
  };

  // State to manage dropdowns individually
  const [dropdownStates, setDropdownStates] = useState({
    analytics: false,
    contentManager: false,
    community: false,
    myAccount: false,
  });

  const toggleDropdown = (dropdownName: string) => {
    setDropdownStates((prevState) => {
      const newState = {
        analytics: false,
        contentManager: false,
        community: false,
        myAccount: false,
      };

      return {
        ...newState,
        [dropdownName]: !prevState[dropdownName],
      };
    });
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div className={style['mobile-menu__container']}>
        <div className={style['mobile-menu__avatarmenu']}>
          <div
            onClick={() => {
              handleMenuClose();
              router.push(`/${user?.username}`);
            }}
          >
            {user?.avatar ? (
              <Avatar
                size={'lg'}
                name={user?.username || ''}
                imageUrl={user?.avatar !== '' ? avatarUrl : ''}
              />
            ) : (
              <LogoIcon
                width={40}
                height={40}
                className={style['mobile-menu__avatarmenu--default']}
              />
            )}
            {user?.username}
          </div>
          <Button
            type="primary-v2"
            onClick={() => {
              handleMenuClose();
              router.push(`/${user?.username}/dashboard/account-settings`);
            }}
            label={'Edit Profile'}
          />
        </div>
        <div className={style['mobile-menu--title']}>Menu</div>
        <div className={style['mobile-menu__menulist']}>
          <div
            className={classNames(style['mobile-menu__menu'], {
              [style['mobile-menu__menu--active']]:
                pathname === `/${user?.username}/dashboard`,
            })}
            onClick={() => {
              handleMenuClose();
              router.push(`/${user?.username}/dashboard`);
            }}
          >
            <div className={classNames(style['mobile-menu__menu--title'])}>
              {' '}
              <Icon icon="dashboardMobile" width={22} height={22} />
              Dashboard
            </div>
          </div>
          <div
            className={classNames(style['mobile-menu__menu'], {
              [style['mobile-menu__menu--active']]:
                pathname === `/${user?.username}/dashboard/stream-manager`,
            })}
            onClick={() => {
              handleMenuClose();
              router.push(`/${user?.username}/dashboard/stream-manager`);
            }}
          >
            <div className={classNames(style['mobile-menu__menu--title'])}>
              {' '}
              <Icon icon="streamManager" width={22} height={22} />
              Stream Manager
            </div>
          </div>
          <div
            className={classNames(style['mobile-menu__menu'], {
              [style['mobile-menu__menu--active']]:
                dropdownStates.contentManager,
            })}
            onClick={() => {
              handleMenuClose();
              router.push(`/${user?.username}/dashboard/manage-events`);
            }}
          >
            <div className={classNames(style['mobile-menu__menu--title'])}>
              {' '}
              <Icon icon="eventMobile" width={22} height={22} />
              Event Manager
            </div>
          </div>

          <div
            className={classNames(style['mobile-menu__menu'], {
              [style['mobile-menu__menu--active']]:
                pathname === `/${user?.username}`,
            })}
            onClick={() => {
              handleMenuClose();
              router.push(`/${user?.username}`);
            }}
          >
            <div className={classNames(style['mobile-menu__menu--title'])}>
              {' '}
              <Icon icon="mychannelMobile" width={22} height={22} />
              My Channel
            </div>
          </div>
          <div
            className={classNames(style['mobile-menu__menu'], {
              [style['mobile-menu__menu--active']]:
                pathname === `/${user?.username}/dashboard/account-settings`,
            })}
            onClick={() => {
              handleMenuClose();
              router.push(`/${user?.username}/dashboard/account-settings`);
            }}
          >
            <div className={classNames(style['mobile-menu__menu--title'])}>
              {' '}
              <Icon icon="settingMobile" width={22} height={22} />
              Settings
            </div>
          </div>
          <div
            className={classNames(style['mobile-menu__menu'], {
              [style['mobile-menu__menu--active']]: pathname === `/contact-us`,
            })}
            onClick={() => {
              handleMenuClose();
              router.push(`/contact-us`);
            }}
          >
            <div className={classNames(style['mobile-menu__menu--title'])}>
              <Icon icon="ContactUssm" width={22} height={22} />
              Contact Us
            </div>
          </div>
          <div
            className={style['mobile-menu__logoutmenu']}
            onClick={async () => {
              await logout();
              try {
                dispatch(setChannel(null));
                dispatch(setUser(null));
              } catch (err) {
                console.log(err);
              }
              timeoutRef.current = setTimeout(() => {
                handleMenuClose();
                router.push('/');
              }, 1000);
            }}
          >
            <div className={classNames(style['mobile-menu__menu--title'])}>
              {' '}
              <Icon icon="logoutMobile" width={22} height={22} />
              Logout
            </div>
          </div>
        </div>
      </div>
      {openMobileModal && width < 840 && (
        <CreateEventMobileModal
          onConfirm={() => setOpenMobileModal(false)}
          onClose={() => setOpenMobileModal(false)}
        />
      )}
    </>
  );
};

export default MobileMenu;
